"use client";

import { useUmamiAnalytics } from "@analytics";
import { LocaleLink, useLocalePathname } from "@i18n/routing";
import { getVideoLinkCards } from "@marketing/shared/lib/video-trend-links";
import { cn } from "@ui/lib";
import { ArrowRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";

/**
 * 热梗页 / 特效页互相推荐的封面卡，按当前页选卡（规则在 getVideoLinkCards）。出片后的结果区和
 * FAQ 前的固定区块共用；固定区块随 SSR 进 HTML，给没出片的人和谷歌看（2026-10-10 老公定两处都放）。
 * 2026-10-15 老公定一行横滑：卡数随页变（热梗页 5、特效页 4，还会涨），一行 2 个总剩孤卡
 */
export function VideoTrendCards({
	placement,
	className,
}: {
	/** 记进 Umami，区分是从结果区还是固定区块点的 */
	placement: "result" | "section";
	className?: string;
}) {
	const t = useTranslations();
	const { trackEvent } = useUmamiAnalytics();
	const pathname = useLocalePathname();
	const items = getVideoLinkCards(pathname);
	if (!items.length) {
		return null;
	}
	return (
		<ul
			className={cn(
				"-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0",
				className,
			)}
		>
			{items.map((item) => (
				<li
					key={item.href}
					// 手机上露出第三张的边提示能滑；桌面上结果卡里窄一档，五张都放得下
					className={cn(
						"w-[44%] shrink-0 snap-start",
						placement === "result"
							? "sm:w-[150px]"
							: "sm:w-[190px]",
					)}
				>
					<LocaleLink
						href={item.href}
						onClick={() =>
							void trackEvent("trend-link-clicked", {
								from: pathname,
								to: item.href,
								placement,
							})
						}
						className="group relative block overflow-hidden rounded-xl bg-gray-3"
					>
						<img
							src={item.poster}
							alt=""
							width={360}
							height={450}
							loading="lazy"
							decoding="async"
							className="aspect-[4/5] w-full object-cover transition-transform duration-300 group-hover:scale-105"
						/>
						<span className="absolute inset-x-0 bottom-0 flex flex-col gap-0.5 bg-gradient-to-t from-black/80 to-transparent px-3 pt-10 pb-2.5 text-white">
							<span className="flex items-center justify-between gap-2 font-medium text-sm">
								{t(item.titleKey)}
								<ArrowRightIcon className="size-4 shrink-0" />
							</span>
							{item.descriptionKey ? (
								<span className="line-clamp-2 text-white/80 text-xs leading-snug">
									{t(item.descriptionKey)}
								</span>
							) : null}
						</span>
					</LocaleLink>
				</li>
			))}
		</ul>
	);
}
